import React, { useContext } from "react";
import styled from "styled-components";
import { Link, useLocation } from "react-router-dom";
import { renderCount } from "../../appHelpers/appLoggers";
import { DarkModeContext } from "../../appContexts/DarkModeContext";
import { DisplayMobileSidebarContext } from "../../appContexts/DisplayMobileSidebarContext";
import { DarkOrLightModeSelectorFC } from "./DarkOrLightModeSelectorFC";
import { NavigatorFC } from "./NavigatorFC";
import { ClientLogoFC } from "./ClientLogoFC";
import { AuthBtnFC } from "../../auth/AuthBtnFC";
import { useAuth0 } from "@auth0/auth0-react";
import { Whisper, Tooltip } from "rsuite";

export const _AsideFC = () => {
  // renderCount(`./components/aside/_AsideFC`);

  const { darkMode } = useContext(DarkModeContext);
  const { displayMobileSidebar } = useContext(DisplayMobileSidebarContext);
  const { isAuthenticated, user } = useAuth0();
  const location = useLocation();

  // Get user initials from name
  const getUserInitials = (name) => {
    if (!name) return "U";
    const nameParts = name.trim().split(" ");
    if (nameParts.length === 1) {
      return nameParts[0].charAt(0).toUpperCase();
    }
    const firstInitial = nameParts[0].charAt(0).toUpperCase();
    const lastInitial = nameParts[nameParts.length - 1].charAt(0).toUpperCase();
    return `${firstInitial}${lastInitial}`;
  };

  return (
    <AsideSC darkMode={darkMode} displayMobileSidebar={displayMobileSidebar}>
      <SidebarContent>
        <TopSection>
          <LogoSection>
            <LogoText>Avante</LogoText>
            <LogoSubtext>Connected</LogoSubtext>
          </LogoSection>
          <NavigatorFC />
        </TopSection>
        <BottomPlacement>
          {isAuthenticated && user && (
            <Whisper
              placement="bottom"
              trigger="hover"
              speaker={<Tooltip>{user.name || "Profile"}</Tooltip>}
            >
              <ProfileLink to={`/admin/user/${user.email}`}>
                <UserProfileSection>
                  <UserInitials>{getUserInitials(user.name)}</UserInitials>
                </UserProfileSection>
              </ProfileLink>
            </Whisper>
          )}
          {/*<DarkModeWrapper>
            <DarkOrLightModeSelectorFC />
          </DarkModeWrapper>*/}
          <AuthButtonWrapper>
            <AuthBtnFC />
          </AuthButtonWrapper>
        </BottomPlacement>
      </SidebarContent>
    </AsideSC>
  );
};

const AsideSC = styled.aside`
  width: 100%;
  height: 70px;
  position: fixed;
  left: 0;
  top: 0;
  z-index: 100;

  background: linear-gradient(
    90deg,
    rgba(44, 90, 160, 0.08) 0%,
    rgba(52, 152, 219, 0.05) 50%,
    rgba(44, 90, 160, 0.08) 100%
  );
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);

  border-bottom: 1px solid rgba(44, 90, 160, 0.15);
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);

  // Hide on mobile, show only on desktop (>= 769px)
  display: none;
  @media screen and (min-width: 769px) {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    padding: 0 32px;
  }

  &::before {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(44, 90, 160, 0.3) 30%,
      rgba(44, 90, 160, 0.3) 70%,
      transparent
    );
  }
`;

const SidebarContent = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: 100%;
  gap: 32px;
`;

const TopSection = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 48px;
`;

const LogoSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  position: relative;

  &::after {
    content: "";
    position: absolute;
    right: -24px;
    top: 20%;
    bottom: 20%;
    width: 1px;
    background: linear-gradient(
      180deg,
      transparent,
      rgba(44, 90, 160, 0.3),
      transparent
    );
  }
`;

const LogoText = styled.div`
  font-size: 22px;
  font-weight: 700;
  color: #2c5aa0;
  letter-spacing: -0.5px;
  line-height: 1;
`;

const LogoSubtext = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: rgba(44, 90, 160, 0.6);
  letter-spacing: 0.5px;
  text-transform: uppercase;
`;

const BottomPlacement = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ProfileLink = styled(Link)`
  text-decoration: none;
  display: flex;
  align-items: center;
`;

const UserProfileSection = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  position: relative;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 2px solid rgba(44, 90, 160, 0.2);
  background: linear-gradient(
    135deg,
    rgba(44, 90, 160, 0.1) 0%,
    rgba(52, 152, 219, 0.05) 100%
  );
  flex-shrink: 0;

  &:hover {
    transform: scale(1.05);
    border-color: rgba(44, 90, 160, 0.4);
    box-shadow: 0 4px 12px rgba(44, 90, 160, 0.2);
  }

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: 12px;
    padding: 2px;
    background: linear-gradient(
      135deg,
      rgba(44, 90, 160, 0.3),
      rgba(52, 152, 219, 0.2)
    );
    -webkit-mask:
      linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    opacity: 0;
    transition: opacity 0.3s;
  }

  &:hover::before {
    opacity: 1;
  }
`;

const UserInitials = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 700;
  color: #2c5aa0;
  background: linear-gradient(
    135deg,
    rgba(44, 90, 160, 0.15) 0%,
    rgba(52, 152, 219, 0.1) 100%
  );
  user-select: none;
`;

const DarkModeWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 12px;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: linear-gradient(
    135deg,
    rgba(44, 90, 160, 0.08) 0%,
    rgba(52, 152, 219, 0.05) 100%
  );

  &:hover {
    background: linear-gradient(
      135deg,
      rgba(44, 90, 160, 0.15) 0%,
      rgba(52, 152, 219, 0.1) 100%
    );
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(44, 90, 160, 0.15);
  }

  svg {
    width: 24px;
    height: 24px;
    cursor: pointer;
  }
`;

const AuthButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;

  button {
    height: 42px;
    padding: 0 16px;
    border-radius: 12px;
    border: none;
    background: linear-gradient(
      135deg,
      rgba(44, 90, 160, 0.12) 0%,
      rgba(52, 152, 219, 0.08) 100%
    );
    color: #2c5aa0;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    display: flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;

    &:hover {
      background: linear-gradient(
        135deg,
        rgba(44, 90, 160, 0.2) 0%,
        rgba(52, 152, 219, 0.15) 100%
      );
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(44, 90, 160, 0.2);
    }

    &:active {
      transform: translateY(0);
    }
  }
`;
